import {useCallback, useEffect, useRef, useState} from "react";
import {Ring} from "@uiball/loaders";
import useSession from "../../../services/state/session/useSession";
import useCheckoutStore from "../../../cart/store/checkoutStore";
import Dialog from "../../general/dialog/Dialog";
import Popup from "../../../../components/general/popup/Popup";
import {request} from "../../../../res/rest/restRequest";
import {api} from "../../../../res/rest/api";
import {env} from "../../../../res/config/env";
import HttpClient from "../../../services/http/HttpClient";
import {idempiereProcess} from "../../../../res/rest/process";
import runAuthRequest from "../../../../redux/util/runAuthRequest";

const POLL_INTERVAL = 5000;
const TIMEOUT = 5 * 60 * 1000;

const callCreatePayment = async (session, paymentTransactionId, consentToken) => {
    const req = request.process(api.PROCESS.YAPILY_CREATE_PAYMENT)
        .param("C_PaymentTransaction_ID", paymentTransactionId);
    if (consentToken) {
        req.param("consentToken", consentToken);
    }
    const url = env.API_URL + req.buildRequest();
    const resp = await HttpClient.client.post(url, req.buildRequestBody(), api.auth(session));
    return idempiereProcess.handleResponseThrowingException(resp);
};

const callGetPaymentStatus = async (session, paymentTransactionId, consentToken) => {
    const req = request.process(api.PROCESS.YAPILY_GET_PAYMENT_STATUS)
        .param("C_PaymentTransaction_ID", paymentTransactionId);
    if (consentToken) {
        req.param("consentToken", consentToken);
    }
    const url = env.API_URL + req.buildRequest();
    const resp = await HttpClient.client.post(url, req.buildRequestBody(), api.auth(session));
    const data = idempiereProcess.handleResponseThrowingException(resp);
    return data.summary;
};

export const PaymentPending = ({ consentToken }) => {

    const {sessionData: session, dispatch} = useSession();
    const paymentTransactionId = useCheckoutStore(state => state.paymentTransactionId);
    const confirmOrder = useCheckoutStore(state => state.confirmOrder);
    const institutions = useCheckoutStore(state => state.institutions);
    const institutionId = useCheckoutStore(state => state.input?.institutionId);
    const selectedBank = institutions?.find(i => i.id === institutionId);

    const [status, setStatus] = useState("creating");
    const [error, setError] = useState(null);
    const [timedOut, setTimedOut] = useState(false);

    const startTime = useRef(Date.now());
    const consentRef = useRef(consentToken);

    // Step 1: Create payment once paymentTransactionId is available
    useEffect(() => {
        if (!session || !paymentTransactionId) return;
        let cancelled = false;

        const createPayment = async () => {
            try {
                await runAuthRequest(
                    session,
                    (s) => callCreatePayment(s, paymentTransactionId, consentRef.current),
                    dispatch
                );
                if (!cancelled) setStatus("polling");
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message || "Failed to create payment. Please try again.");
                    setStatus("failed");
                }
            }
        };

        createPayment();
        return () => { cancelled = true; };
    }, [session, dispatch, paymentTransactionId]);

    // Step 2: Poll payment status
    const pollStatus = useCallback(async () => {
        if (!session || !paymentTransactionId) return;
        try {
            const result = await runAuthRequest(
                session,
                (s) => callGetPaymentStatus(s, paymentTransactionId, consentRef.current),
                dispatch
            );
            if (result.includes("ACSC") || result.includes("COMPLETED")) {
                setStatus("completed");
                confirmOrder();
            } else if (result.includes("FAILED") || result.includes("REJECTED")) {
                setError("Payment was declined by your bank. Please try again.");
                setStatus("failed");
            }
        } catch (e) {
        }
    }, [session, dispatch, paymentTransactionId, confirmOrder]);

    useEffect(() => {
        if (status !== "polling") return;

        const interval = setInterval(() => {
            if (Date.now() - startTime.current > TIMEOUT) {
                setTimedOut(true);
                clearInterval(interval);
                return;
            }
            pollStatus();
        }, POLL_INTERVAL);

        return () => clearInterval(interval);
    }, [pollStatus, status]);

    if (timedOut) {
        return (
            <>
                <p className="cart-h2">Payment</p>
                <p>We didn't receive payment confirmation in time.</p>
                <p>If you completed the payment, please check your order history or contact support.</p>
                <p><a href="/checkout">Return to checkout</a></p>
            </>
        );
    }

    if (status === "failed") {
        return (
            <>
                <p className="cart-h2">Payment</p>
                <p>{error}</p>
                <p><a href="/checkout">Return to checkout to try again</a></p>
            </>
        );
    }

    const message = status === "creating"
        ? "Creating your payment..."
        : "Waiting for payment confirmation from your bank...";

    return (
        <Popup>
            <Dialog>
                <div className="loading-dialog-content">
                    {selectedBank?.icon
                        ? <img
                            src={selectedBank.icon}
                            alt={selectedBank.fullName}
                            style={{ width: 50, height: 50, objectFit: "contain" }}
                        />
                        : <Ring size={50} lineWeight={4} speed={2} color="var(--secondary)" />
                    }
                    <div>
                        {selectedBank &&
                            <p className="loading-dialog-title">{selectedBank.fullName}</p>
                        }
                        <p>{message}</p>
                    </div>
                </div>
            </Dialog>
        </Popup>
    );
};
